import { mentionedRefs } from "@repo/mentions";
import { Spinner } from "@repo/ui/components/kibo-ui/spinner";
import { Markdown } from "@repo/ui/components/markdown";
import {
  Timeline,
  TimelineContent,
  TimelineDate,
  TimelineHeader,
  TimelineIndicator,
  TimelineItem,
} from "@repo/ui/components/reui/timeline";
import { Avatar, AvatarFallback } from "@repo/ui/components/ui/avatar";
import { Switch } from "@repo/ui/components/ui/switch";
import { Tooltip, TooltipContent, TooltipTrigger } from "@repo/ui/components/ui/tooltip";
import { cn } from "@repo/ui/lib/utils";
import { Sparkles } from "lucide-react";
import { memo, useEffect, useMemo, useRef, useState } from "react";
import { ActivityEventItem } from "@/features/tasks/components/activity-event-item";
import type { ActivityEvent } from "@/features/tasks/lib/task-activity";
import { mergeTimeline } from "@/features/tasks/lib/task-activity";
import { formatDateTime, formatRelativeTime } from "@/shared/lib/date-formats";
import { getInitials } from "@/shared/lib/initials";

export type TaskNote = {
  authorString?: string;
  authorReference?: { reference?: string; display?: string };
  time?: string;
  text: string;
  extension?: Array<{ url: string; valueReference?: { reference?: string } }>;
};

export function renderCommentText(note: TaskNote) {
  const labels = new Set(
    mentionedRefs(note)
      .map((r) => r.display)
      .filter((d): d is string => !!d),
  );
  if (!labels.size) return note.text;
  const escaped = [...labels].map((l) => l.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")).sort((a, b) => b.length - a.length);
  const pattern = new RegExp(`(@(?:${escaped.join("|")}))`, "g");
  const parts = note.text.split(pattern);
  return parts.map((part, index) =>
    part.startsWith("@") && labels.has(part.slice(1)) ? (
      <span
        key={`${index}-${part}`}
        className="inline-flex items-center rounded-full bg-accent px-1.5 py-0.5 text-xs font-medium text-accent-foreground"
      >
        {part.slice(1)}
      </span>
    ) : (
      part
    ),
  );
}

export const NoteItem = memo(
  ({ note, index, isAi, isHighlighted }: { note: TaskNote; index: number; isAi: boolean; isHighlighted?: boolean }) => {
    const display = note.authorReference?.display ?? note.authorString;
    const ref = useRef<HTMLDivElement>(null);

    // Scroll the anchored note into view once. Only the inbox passes a
    // highlight, and only for kinds whose dedup key carries an occurrence.
    useEffect(() => {
      if (isHighlighted) ref.current?.scrollIntoView({ block: "center", behavior: "smooth" });
    }, [isHighlighted]);

    return (
      <TimelineItem
        ref={ref}
        step={index + 1}
        className={cn(
          "has-[+[data-completed]]:[&_[data-slot=timeline-separator]]:bg-foreground/20 group-data-[orientation=vertical]/timeline:not-last:pb-0",
          isHighlighted && "-mx-2 rounded-md bg-primary/5 px-2 ring-1 ring-primary/20",
        )}
      >
        <TimelineHeader className="flex items-center gap-2.5">
          <div
            aria-hidden
            className="absolute -left-6 -translate-x-1/2 top-7 -bottom-1 w-px bg-border group-last/timeline-item:hidden"
          />
          <TimelineIndicator className="size-5 border-none bg-transparent">
            {isAi ? (
              <div className="flex size-5 items-center justify-center rounded-full bg-primary/10">
                <Sparkles className="size-3 text-primary" />
              </div>
            ) : (
              <Avatar className="size-5">
                <AvatarFallback className="text-[9px]">{display ? getInitials(display) : "?"}</AvatarFallback>
              </Avatar>
            )}
          </TimelineIndicator>
          <TimelineDate className="text-muted-foreground/60 mb-0 text-xs uppercase">
            <span className="flex items-center gap-1.5">
              {note.time ? (
                <Tooltip>
                  <TooltipTrigger>{formatRelativeTime(note.time)}</TooltipTrigger>
                  <TooltipContent>{formatDateTime(note.time)}</TooltipContent>
                </Tooltip>
              ) : (
                ""
              )}
            </span>
          </TimelineDate>
        </TimelineHeader>
        <TimelineContent className={cn("text-muted-foreground text-sm break-words", isAi && "rounded-md px-3 py-2")}>
          {isAi ? <Markdown parseIncompleteMarkdown={false}>{note.text}</Markdown> : renderCommentText(note)}
        </TimelineContent>
      </TimelineItem>
    );
  },
  (prev, next) =>
    prev.note.text === next.note.text &&
    prev.isAi === next.isAi &&
    prev.isHighlighted === next.isHighlighted &&
    (prev.note.authorReference?.display ?? prev.note.authorString) ===
      (next.note.authorReference?.display ?? next.note.authorString),
);

export function TaskCommentList({
  notes,
  activityEvents = [],
  aiDeviceRef,
  isAwaitingAi,
  streamingText,
  isStreaming,
  showHeader = true,
  highlightNoteTime,
}: {
  notes: TaskNote[];
  activityEvents?: ActivityEvent[];
  aiDeviceRef?: string;
  isAwaitingAi: boolean;
  streamingText: string;
  isStreaming: boolean;
  showHeader?: boolean;
  /** `Annotation.time` of the note to anchor and highlight — the inbox passes
   *  the occurrence off a `mentioned` item's dedup key so opening an item lands
   *  on the note that caused it, not just the Task. */
  highlightNoteTime?: string;
}) {
  const [commentsOnly, setCommentsOnly] = useState(false);
  const allEntries = useMemo(() => mergeTimeline(notes, activityEvents), [notes, activityEvents]);
  const entries = useMemo(
    () => (commentsOnly ? allEntries.filter((e) => e.type === "comment") : allEntries),
    [allEntries, commentsOnly],
  );

  const lastNote = notes[notes.length - 1];
  const lastNoteIsAi = !!aiDeviceRef && lastNote?.authorReference?.reference === aiDeviceRef;
  const showAiSlot = isAwaitingAi || !!streamingText;
  const skipLastNote = !isAwaitingAi && lastNoteIsAi && !!streamingText;

  return (
    <div className="space-y-4">
      {showHeader && (
        <div className="flex items-center justify-between">
          <h2 className="text-sm font-medium text-muted-foreground">Activity</h2>
          <label htmlFor="task-timeline-comments-only" className="flex items-center gap-1.5 cursor-pointer select-none">
            <span className="text-xs text-muted-foreground/60">Comments only</span>
            <Switch
              id="task-timeline-comments-only"
              size="sm"
              checked={commentsOnly}
              onCheckedChange={setCommentsOnly}
            />
          </label>
        </div>
      )}
      {entries.length === 0 && !showAiSlot ? (
        <p className="text-sm text-muted-foreground/60 py-4">No activity yet</p>
      ) : (
        <Timeline defaultValue={0} className="gap-2.5">
          {entries.map((entry, idx) => {
            if (entry.type === "event") {
              return (
                <ActivityEventItem
                  key={`ev-${entry.event.kind}-${entry.event.timestamp}`}
                  event={entry.event}
                  step={idx + 1}
                />
              );
            }
            if (skipLastNote && entry.noteIndex === notes.length - 1) return null;
            const isAi = aiDeviceRef ? entry.note.authorReference?.reference === aiDeviceRef : false;
            return (
              <NoteItem
                key={`note-${entry.noteIndex}`}
                note={entry.note}
                index={idx}
                isAi={isAi}
                isHighlighted={!!highlightNoteTime && entry.note.time === highlightNoteTime}
              />
            );
          })}
          {showAiSlot && (
            <TimelineItem
              key="ai-response"
              step={isAwaitingAi ? entries.length + 1 : entries.length}
              className="group-data-[orientation=vertical]/timeline:not-last:pb-0"
            >
              <TimelineHeader className="flex items-center gap-2.5">
                <div
                  aria-hidden
                  className="absolute -left-6 -translate-x-1/2 top-7 -bottom-1 w-px bg-border group-last/timeline-item:hidden"
                />
                <TimelineIndicator className="size-5 border-none bg-transparent">
                  <div className="flex size-5 items-center justify-center rounded-full bg-primary/10">
                    <Sparkles className="size-3 text-primary" />
                  </div>
                </TimelineIndicator>
                <TimelineDate className="text-muted-foreground/60 mb-0 text-xs">
                  <span className="flex items-center gap-1.5">
                    {!isAwaitingAi && lastNote?.time ? (
                      <Tooltip>
                        <TooltipTrigger>{formatRelativeTime(lastNote.time)}</TooltipTrigger>
                        <TooltipContent>{formatDateTime(lastNote.time)}</TooltipContent>
                      </Tooltip>
                    ) : (
                      "just now"
                    )}
                  </span>
                </TimelineDate>
              </TimelineHeader>
              <TimelineContent className={cn("text-muted-foreground text-sm", streamingText && "rounded-md px-3 py-2")}>
                {streamingText ? (
                  <Markdown isAnimating={isStreaming}>{streamingText}</Markdown>
                ) : (
                  <Spinner variant="ellipsis" size={20} />
                )}
              </TimelineContent>
            </TimelineItem>
          )}
        </Timeline>
      )}
    </div>
  );
}
